import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  FiChevronDown,
  FiMenu,
  FiMessageSquare,
  FiMoon,
  FiSearch,
  FiSun,
  FiUser,
  FiX,
} from 'react-icons/fi';
import { PiNotification } from 'react-icons/pi';
import GroupedButton from '../../../../../layout/ReusableComponent/Buttons/GroupedButton/GroupedButton';
import MessagingPage from '../../../../MainHeader/Messaging/MessagingPage';
import MessagingMobileModal from '../../../../MainHeader/Messaging/MessagingMobileModal';
import NotificationsDropdown from '../../../../MainHeader/Notifications/Notifications';
import MyProfileDropdown from '../../../../MainHeader/Profile/MyProfile';
import { APP_NAME, LOGO_ICON_SRC } from '../../../../../config/branding';
import ShellHeaderProductsNav from '../../../../../layout/ShellHeaderProductsNav';
import { GUEST_CREATE_ACCOUNT_LABEL } from '../../auth/guestAuth';
import { buildSearchUrl } from '../../../../search/searchRouting';
import { applyProfileAvatarImgFallback } from '../../../../MainHeader/profilePictureUrl';

/**
 * Mobile shell chrome matching /posts: sticky bar with menu · logo · search · more (…).
 */
export default function PostsMobileGroupedShellChrome({
  shellHeaderRef,
  shellNavIcon,
  shellNavLabel,
  shellNavLayoutReady,
  toggleShellNav,
  mobileDrawerOpen,
  setMobileDrawerOpen,
  feedSearchQ,
  setFeedSearchQ,
  onSearchChange,
  handleSearchKeyDown,
  mobileSearchInContext = false,
  onMobileSearchGo,
  themeMode,
  setThemeMode,
  isGuestViewer,
  profile,
  profileAvatarSrc,
  returnTo,
  onGuestSignUp,
  onManageProfileClick,
  baseUrl,
  onMobileChromeCloseDrawer,
  notificationsPageActive = false,
  messagesPageActive = false,
}) {
  const navigate = useNavigate();
  const handleMobileSearchChange = onSearchChange ?? ((e) => setFeedSearchQ(e.target.value));
  const [mobileShellSearchRowOpen, setMobileShellSearchRowOpen] = useState(false);
  const [mobileShellOverlay, setMobileShellOverlay] = useState(null);
  const mobileSearchInputRef = useRef(null);

  const closeMobileShell = useCallback(() => setMobileShellOverlay(null), []);

  const openPeerProfileFromMessaging = useCallback(
    (peer) => {
      const uid = String(peer?.userId || '').trim();
      if (!uid) return;
      const mode =
        String(peer?.profileMode || 'personal').trim().toLowerCase() === 'social' ? 'social' : 'personal';
      const handle = String(peer?.handle || '').trim();
      setMobileShellOverlay(null);
      const params = new URLSearchParams({
        view: 'user-profile',
        userId: uid,
        profileMode: mode,
      });
      if (handle) params.set('handle', handle);
      navigate(`/posts?${params.toString()}`);
    },
    [navigate]
  );

  const closeProfileChrome = useCallback(() => {
    setMobileShellOverlay(null);
    onManageProfileClick?.();
  }, [onManageProfileClick]);

  useEffect(() => {
    if (!mobileShellOverlay) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow || '';
    };
  }, [mobileShellOverlay]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (mobileShellOverlay) setMobileShellOverlay(null);
      else if (mobileShellSearchRowOpen) setMobileShellSearchRowOpen(false);
      else if (mobileDrawerOpen) setMobileDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileShellOverlay, mobileShellSearchRowOpen, mobileDrawerOpen, setMobileDrawerOpen]);

  const mobileShellGroupedItems = useMemo(
    () => [
      ...(!notificationsPageActive && !messagesPageActive
        ? [
            {
              id: 'mobile-messages',
              text: 'Messages',
              icon: <FiMessageSquare aria-hidden />,
              onClick: () => {
                setMobileDrawerOpen(false);
                setMobileShellOverlay('messages');
              },
            },
            {
              id: 'mobile-notifications',
              text: 'Notifications',
              icon: <PiNotification aria-hidden />,
              onClick: () => {
                setMobileDrawerOpen(false);
                setMobileShellOverlay('notifications');
              },
            },
          ]
        : !notificationsPageActive
          ? [
              {
                id: 'mobile-notifications',
                text: 'Notifications',
                icon: <PiNotification aria-hidden />,
                onClick: () => {
                  setMobileDrawerOpen(false);
                  setMobileShellOverlay('notifications');
                },
              },
            ]
          : !messagesPageActive
            ? [
                {
                  id: 'mobile-messages',
                  text: 'Messages',
                  icon: <FiMessageSquare aria-hidden />,
                  onClick: () => {
                    setMobileDrawerOpen(false);
                    setMobileShellOverlay('messages');
                  },
                },
              ]
            : []),
      {
        id: 'mobile-theme',
        text: themeMode === 'light' ? 'Dark mode' : 'Light mode',
        icon: themeMode === 'light' ? <FiMoon aria-hidden /> : <FiSun aria-hidden />,
        onClick: () => setThemeMode((t) => (t === 'light' ? 'dark' : 'light')),
      },
      ...(isGuestViewer
        ? [
            {
              id: 'mobile-signup',
              text: GUEST_CREATE_ACCOUNT_LABEL,
              icon: <FiUser aria-hidden />,
              onClick: onGuestSignUp,
            },
          ]
        : [
            {
              id: 'mobile-profile',
              text: 'Account',
              icon: <FiUser aria-hidden />,
              onClick: () => setMobileShellOverlay('profile'),
            },
          ]),
    ],
    [
      notificationsPageActive,
      messagesPageActive,
      themeMode,
      isGuestViewer,
      onGuestSignUp,
      setMobileDrawerOpen,
      setThemeMode,
    ]
  );

  const mobileShellMoreTriggerIcon = useMemo(() => {
    if (!profile?.userId) {
      return <FiChevronDown aria-hidden />;
    }
    return (
      <img
        src={profileAvatarSrc}
        alt=""
        className="support-open-disc-shell-header-avatar posts-page-shell-more-gb__avatar"
        onError={(e) => {
          applyProfileAvatarImgFallback(e.currentTarget, {
            userId: profile?.userId,
            name: profile?.displayName || profile?.name,
          });
        }}
      />
    );
  }, [profile?.userId, profileAvatarSrc]);

  const mobileShellTitle =
    mobileShellOverlay === 'messages'
      ? 'Messages'
      : mobileShellOverlay === 'notifications'
        ? 'Notifications'
        : mobileShellOverlay === 'profile'
          ? 'Account'
          : '';

  const defaultSearchGo = useCallback(() => {
    const q = String(feedSearchQ || '').trim();
    navigate(buildSearchUrl({ q }));
    setMobileShellSearchRowOpen(false);
  }, [feedSearchQ, navigate]);

  const handleSearchGo = onMobileSearchGo ?? defaultSearchGo;

  const shellNavIconNode = shellNavIcon ?? <FiMenu className="support-open-disc-shell-menu-icon" aria-hidden />;

  const isMobileMessagesModalOpen = mobileShellOverlay === 'messages';

  const mobileAppChrome = (
    <div
      ref={shellHeaderRef}
      className="support-open-disc-shell-mobile-chrome"
      onClick={onMobileChromeCloseDrawer}
    >
      <div className="support-open-disc-shell-header support-open-disc-shell-header--mobile-single">
        <div className="support-open-disc-shell-header-left">
          <ShellHeaderProductsNav />
          <button
            type="button"
            className="support-open-disc-shell-header-btn noOutline-text"
            onClick={shellNavLayoutReady ? toggleShellNav : undefined}
            aria-expanded={mobileDrawerOpen}
            aria-label={shellNavLabel}
            disabled={!shellNavLayoutReady}
          >
            {shellNavIconNode}
          </button>
          <img src={LOGO_ICON_SRC} alt={APP_NAME} className="support-open-disc-shell-logo-image" />
          <div className="support-open-disc-shell-logo-text">{APP_NAME}</div>
        </div>
        <div className="support-open-disc-shell-header-right support-open-disc-shell-header-right--mobile-tools">
          <button
            type="button"
            className={`support-open-disc-shell-header-btn support-open-disc-shell-mobile-search-trigger noOutline-text${
              mobileShellSearchRowOpen ? ' is-active' : ''
            }`}
            aria-label={mobileShellSearchRowOpen ? 'Hide search' : 'Show search'}
            aria-expanded={mobileShellSearchRowOpen}
            onClick={() => {
              if (mobileShellSearchRowOpen) {
                setMobileShellSearchRowOpen(false);
                return;
              }
              setMobileDrawerOpen(false);
              flushSync(() => {
                setMobileShellSearchRowOpen(true);
              });
              mobileSearchInputRef.current?.focus({ preventScroll: true });
            }}
          >
            <FiSearch className="support-open-disc-shell-mobile-search-trigger-icon" aria-hidden />
          </button>
          <GroupedButton
            className={`posts-page-shell-more-gb${profile?.userId ? ' posts-page-shell-more-gb--avatar' : ''}`}
            align="right"
            ariaLabel={profile?.userId ? 'Account menu' : 'More options'}
            triggerIcon={mobileShellMoreTriggerIcon}
            triggerText=""
            items={mobileShellGroupedItems}
          />
        </div>
      </div>
      {mobileShellSearchRowOpen ? (
        <div
          className={`support-open-disc-shell-mobile-search-row${
            !mobileSearchInContext ? ' support-open-disc-shell-mobile-search-row--has-action' : ''
          }`}
          role="search"
        >
          <FiSearch className="support-open-disc-shell-mobile-search-row-icon" aria-hidden />
          <input
            ref={mobileSearchInputRef}
            type="search"
            className="support-open-disc-shell-mobile-search-input"
            value={feedSearchQ}
            onChange={handleMobileSearchChange}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search posts, people, and more..."
            aria-label="Search posts, people, and more"
            autoComplete="off"
          />
          {!mobileSearchInContext ? (
            <button
              type="button"
              className="posts-mobile-fs__primary support-open-disc-shell-mobile-search-go"
              onClick={handleSearchGo}
            >
              Search
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {!isMobileMessagesModalOpen ? mobileAppChrome : null}

      <MessagingMobileModal open={isMobileMessagesModalOpen} appChrome={mobileAppChrome}>
        <MessagingPage
          embedded
          mobileFullscreen
          viewerUserId={profile?.userId}
          onViewPeerProfile={openPeerProfileFromMessaging}
        />
      </MessagingMobileModal>

      {mobileShellOverlay && mobileShellOverlay !== 'messages'
        ? createPortal(
            <div
              className="posts-mobile-fs"
              role="dialog"
              aria-modal="true"
              aria-labelledby="posts-mobile-fs-title"
            >
              <div className="posts-mobile-fs__panel">
                <div className="posts-mobile-fs__top">
                  <button
                    type="button"
                    className="posts-mobile-fs__close"
                    onClick={closeMobileShell}
                    aria-label="Close"
                  >
                    <FiX aria-hidden />
                  </button>
                  <h1 id="posts-mobile-fs-title" className="posts-mobile-fs__title">
                    {mobileShellTitle}
                  </h1>
                </div>
                <div className="posts-mobile-fs__scroll">
                  {mobileShellOverlay === 'notifications' ? (
                    <div className="posts-mobile-fs__embed">
                      <NotificationsDropdown
                        portaled={false}
                        baseUrl={baseUrl}
                        onNavigateToAll={() => {
                          closeMobileShell();
                          navigate('/notifications');
                        }}
                      />
                    </div>
                  ) : null}
                  {mobileShellOverlay === 'profile' && !isGuestViewer ? (
                    <div className="posts-mobile-fs__embed">
                      <MyProfileDropdown guestBrowse={false} onManageProfileClick={closeProfileChrome} />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
